import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import axios from "axios";
import { API_BASE_URL } from '../../constants/api';
import { Riple } from "react-loading-indicators";
import scooterIcon from '../../assets/img/icons/scooter.png';
import wheelchairIcon from '../../assets/img/icons/wheelchair.png';
import tileBackground from '../../assets/img/backgrounds/select-type-tile.png';

const getSlotsFromVehicleResponse = (data) => {
    const slots = [];
    Object.values(data).forEach(vehicleData => {
        Object.values(vehicleData.slots || {}).forEach(slot => slots.push(slot));
    });
    return slots;
};

const ScooterTypeSelect = () => {

    const { eventId } = useParams();
    const navigate = useNavigate();
    const [eventDetails, setEventDetails] = useState(null);
    const [availabilityByType, setAvailabilityByType] = useState({
        scooter: null,
        wheelchair: null,
    });
    const [eventLoading, setEventLoading] = useState(true);
    const [availabilityLoading, setAvailabilityLoading] = useState(true);
    const [now, setNow] = useState(() => new Date());
    const isLoading = eventLoading || availabilityLoading;
    const isAdmin = !!localStorage.getItem('ambassador_token');

    const slotHasAvailability = (slot) => (
        isAdmin ? slot.has_availability : slot.has_public_availability
    );

    useEffect(() => {
        const fetchEventDetails = async () => {
            try {
                const response = await axios.get(`${API_BASE_URL}/api/events/${eventId}/details?key=7678356c642c91a255471d162205e0f3`);
                setEventDetails(response.data.data);
            } catch (error) {
                console.error('Error fetching event details:', error);
            } finally {
                setEventLoading(false);
            }
        };
        fetchEventDetails();
    }, [eventId]);

    useEffect(() => {
        const fetchAvailability = async (vehicleType) => {
            try {
                const checkLimit = localStorage.getItem('ambassador_token') ? 'admin' : 'public';
                const response = await axios.get(
                    `${API_BASE_URL}/api/events/${eventId}/slots?key=7678356c642c91a255471d162205e0f3&format=vehicles&vehicle_type=${vehicleType}&check_limit=${checkLimit}`
                );
                const slots = getSlotsFromVehicleResponse(response.data.data);
                return slots.some(slotHasAvailability);
            } catch (error) {
                console.error(`Error fetching ${vehicleType} availability:`, error);
                return null;
            }
        };

        const loadAvailability = async () => {
            const [scooterAvailable, wheelchairAvailable] = await Promise.all([
                fetchAvailability('scooter'),
                fetchAvailability('wheelchair'),
            ]);
            setAvailabilityByType({
                scooter: scooterAvailable,
                wheelchair: wheelchairAvailable,
            });
            setAvailabilityLoading(false);
        };

        loadAvailability();
    }, [eventId]);

    useEffect(() => {
        const timer = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    const handleSelectType = (vehicleType) => {
        if (availabilityByType[vehicleType] === false) return;
        navigate(`/scooter/event/${eventId}/${vehicleType}/vehicles`);
    };

    const formatDate = (date) => {
        if (!date) return '';
        return new Date(date).toLocaleDateString('en-GB', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const formatEventDate = (start, end) => {
        if (!start) return '';
        const startStr = formatDate(start);
        if (!end) return startStr;
        const sameDay = new Date(start).toDateString() === new Date(end).toDateString();
        return sameDay ? startStr : `${startStr} – ${formatDate(end)}`;
    };

    const formatBookingOpenTime = (date) => {
        const d = new Date(date);
        const hours = d.getHours();
        const minutes = d.getMinutes();
        const period = hours >= 12 ? 'pm' : 'am';
        const hour12 = hours % 12 === 0 ? 12 : hours % 12;
        const timeStr = minutes === 0 ? `${hour12}${period}` : `${hour12}:${String(minutes).padStart(2, '0')}${period}`;
        return `${timeStr} on ${formatDate(d).replace(',', '')}`;
    };

    const formatCountdown = (ms) => {
        const totalSeconds = Math.max(0, Math.floor(ms / 1000));
        const days = Math.floor(totalSeconds / 86400);
        const hours = Math.floor((totalSeconds % 86400) / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        return `${days} days ${hours} hours ${minutes} minutes ${seconds} seconds`;
    };

    const bookingOpensAt = eventDetails?.show_booking_form_at ? new Date(eventDetails.show_booking_form_at) : null;
    const bookingNotYetOpen = bookingOpensAt && bookingOpensAt > now;

    const tiles = [
        { type: 'scooter', label: 'Select scooter', icon: scooterIcon },
        { type: 'wheelchair', label: 'Select wheelchair', icon: wheelchairIcon },
    ];

    if (!eventLoading && eventDetails && !eventDetails.scooter_booking_enabled) {
        return null;
    }

    return (
        <div className="d-flex flex-column justify-content-between min-vh-100" id="body-container">
            <Header />

            <main>
                <section className="bg-motability-mid-black-5 py-32 pt-lg-96 pb-lg-148">
                    <div className="container position-relative">
                        {isLoading ? (
                            <div className="d-flex justify-content-center align-items-center min-vh-50">
                                <Riple color="#0052BE" size="medium" text="" textColor="" />
                            </div>
                        ) : (
                        <>
                        <a
                            href="https://cms.motabilityschemelive.co.uk/customers/login"
                            className="small fw-semibold text-decoration-none position-absolute top-0 end-0 d-none d-lg-block"
                            style={{ color: '#FF5500' }}
                        >
                            Already booked? - Manage your bookings
                        </a>

                        {eventDetails?.name && (
                            <>
                                <p className="text-center text-motability-black fw-semibold mb-2">
                                    {eventDetails.name}{eventDetails.address ? ` – ${eventDetails.address}` : ''}
                                </p>
                                {eventDetails.start_at && (
                                    <p className="text-center text-motability-black mb-2">
                                        {formatEventDate(eventDetails.start_at, eventDetails.end_at)}
                                    </p>
                                )}
                            </>
                        )}

                        <div className="mb-4 mb-lg-5">
                            <h1 className="text-primary text-center mb-0">
                                Select scooter or wheelchair
                            </h1>
                            <a
                                href="https://cms.motabilityschemelive.co.uk/customers/login"
                                className="small fw-semibold text-decoration-none d-block text-center mt-2 d-lg-none"
                                style={{ color: '#FF5500' }}
                            >
                                Already booked? - Manage your bookings
                            </a>
                        </div>

                        {bookingNotYetOpen ? (
                            <div className="bg-motability-white border border-motability-light-blue rounded-4 p-4 p-lg-32 text-center">
                                <p className="text-motability-black fw-semibold mb-2">
                                    You will be able to book a scooter and manual wheelchair from {formatBookingOpenTime(bookingOpensAt)}.
                                </p>
                                <p className="text-motability-black mb-0">
                                    Please come back to this page in {formatCountdown(bookingOpensAt - now)}.
                                </p>
                            </div>
                        ) : (
                        <div className="row g-4 g-lg-5 justify-content-center">
                            {tiles.map((tile) => {
                                const isFullyBooked = availabilityByType[tile.type] === false;

                                return (
                                    <div key={tile.type} className="col-12 col-md-6 col-lg-5">
                                        <button
                                            type="button"
                                            className="w-100 border-0 rounded-4 overflow-hidden p-4 p-lg-5 d-flex flex-column align-items-center justify-content-between"
                                            style={{
                                                aspectRatio: '1 / 1',
                                                backgroundImage: `url(${tileBackground})`,
                                                backgroundSize: 'cover',
                                                backgroundPosition: 'center',
                                                backgroundRepeat: 'no-repeat',
                                                opacity: isFullyBooked ? 0.7 : 1,
                                                cursor: isFullyBooked ? 'not-allowed' : 'pointer',
                                            }}
                                            onClick={() => handleSelectType(tile.type)}
                                            disabled={isFullyBooked}
                                            aria-label={isFullyBooked ? `${tile.label} – Fully booked` : tile.label}
                                        >
                                            {isFullyBooked && (
                                                <span className="badge bg-danger text-white px-3 py-2 rounded-pill ff-f37-maulstick mb-3">
                                                    Fully booked
                                                </span>
                                            )}
                                            <span className="d-flex align-items-center justify-content-center flex-grow-1">
                                                <img
                                                    src={tile.icon}
                                                    alt=""
                                                    aria-hidden="true"
                                                    style={{ width: '206px', maxWidth: '100%', height: 'auto' }}
                                                />
                                            </span>
                                            <span className="text-motability-white fw-semibold fs-4 mb-2">
                                                {tile.label}
                                            </span>
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                        )}

                        <div className="d-flex flex-column align-items-start mt-4 mt-lg-64">
                            <Link to="/scooter-event-select" className="d-inline-block fw-medium text-decoration-none">
                                ‹ <span className="d-inline-block text-decoration-underline">Back to event selection</span>
                            </Link>
                        </div>
                        </>
                        )}
                    </div>
                </section>
            </main>

            <Footer />
        </div>
    );
};

export default ScooterTypeSelect;
